/**
 * RumahKira design system
 * Clean, flat, professional look: one brand colour (indigo), neutral slate greys,
 * 1px borders instead of heavy shadows, Inter typography with tabular numbers.
 */

import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { createTheme, ThemeProvider, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const MODE_KEY = 'darkMode';

export const createAppTheme = (mode = 'light') => {
  const dark = mode === 'dark';

  const primaryMain = dark ? '#818CF8' : '#4F46E5';
  const divider = dark ? '#1F2937' : '#E5E7EB';
  const paper = dark ? '#111827' : '#FFFFFF';
  const background = dark ? '#0B1120' : '#F5F6F8';

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        main: primaryMain,
        light: dark ? '#A5B4FC' : '#6366F1',
        dark: dark ? '#6366F1' : '#4338CA',
        contrastText: dark ? '#0B1120' : '#FFFFFF',
      },
      secondary: {
        main: dark ? '#FBBF24' : '#D97706',
        contrastText: '#FFFFFF',
      },
      success: { main: dark ? '#4ADE80' : '#16A34A' },
      warning: { main: dark ? '#FBBF24' : '#D97706' },
      error: { main: dark ? '#F87171' : '#DC2626' },
      info: { main: dark ? '#38BDF8' : '#0284C7' },
      background: { default: background, paper },
      text: {
        primary: dark ? '#F1F5F9' : '#0F172A',
        secondary: dark ? '#94A3B8' : '#475569',
        disabled: dark ? '#475569' : '#94A3B8',
      },
      divider,
      action: {
        hover: dark ? 'rgba(148, 163, 184, 0.08)' : 'rgba(15, 23, 42, 0.04)',
        selected: alpha(primaryMain, dark ? 0.16 : 0.08),
      },
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: ['Inter', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto', 'sans-serif'].join(','),
      h1: { fontWeight: 800, letterSpacing: '-0.03em' },
      h2: { fontWeight: 800, letterSpacing: '-0.025em' },
      h3: { fontWeight: 700, fontSize: '2rem', letterSpacing: '-0.02em' },
      h4: { fontWeight: 700, fontSize: '1.625rem', letterSpacing: '-0.02em' },
      h5: { fontWeight: 700, fontSize: '1.25rem', letterSpacing: '-0.01em' },
      h6: { fontWeight: 600, fontSize: '1.0625rem', letterSpacing: '-0.005em' },
      subtitle1: { fontWeight: 600, fontSize: '0.975rem' },
      subtitle2: { fontWeight: 600, fontSize: '0.875rem' },
      body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
      body2: { fontSize: '0.875rem', lineHeight: 1.55 },
      caption: { fontSize: '0.75rem', lineHeight: 1.45 },
      overline: { fontWeight: 600, fontSize: '0.6875rem', letterSpacing: '0.08em', lineHeight: 1.6 },
      button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
    },
  });

  return createTheme(theme, {
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: background,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          '.tabular': { fontVariantNumeric: 'tabular-nums' },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 10, paddingInline: 16 },
          sizeLarge: { paddingBlock: 11, paddingInline: 22, fontSize: '0.975rem' },
          sizeSmall: { borderRadius: 8 },
          outlined: { borderColor: divider, backgroundColor: paper },
        },
      },
      MuiIconButton: {
        styleOverrides: { root: { borderRadius: 10 } },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: divider },
        },
      },
      MuiCard: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: { root: { borderRadius: 14, borderColor: divider } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: paper,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: dark ? '#334155' : '#D1D5DB' },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: { root: { fontWeight: 500 } },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 600 },
          sizeSmall: { height: 24, fontSize: '0.75rem' },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderColor: divider,
            color: theme.palette.text.secondary,
            '&.Mui-selected': {
              color: primaryMain,
              backgroundColor: alpha(primaryMain, dark ? 0.16 : 0.08),
              borderColor: alpha(primaryMain, 0.5),
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: 600, minHeight: 44 } },
      },
      MuiTabs: {
        styleOverrides: { root: { minHeight: 44 }, indicator: { height: 3, borderRadius: 3 } },
      },
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: 16, backgroundImage: 'none' } },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 10, alignItems: 'center' } },
      },
      MuiLinearProgress: {
        styleOverrides: { root: { borderRadius: 4, height: 6 }, bar: { borderRadius: 4 } },
      },
      MuiListItemButton: {
        styleOverrides: { root: { borderRadius: 10 } },
      },
      MuiAccordion: {
        defaultProps: { disableGutters: true, elevation: 0 },
        styleOverrides: {
          root: {
            border: `1px solid ${divider}`,
            borderRadius: 14,
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: { tooltip: { fontSize: '0.75rem', borderRadius: 8 } },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: { minWidth: 0, paddingInline: 4, color: theme.palette.text.secondary },
          label: { fontSize: '0.7rem', fontWeight: 600, '&.Mui-selected': { fontSize: '0.7rem' } },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: divider },
          head: { fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.75rem' },
        },
      },
    },
  });
};

// ---------------------------------------------------------------------------
// Colour mode (light / dark) context
// ---------------------------------------------------------------------------

const ColorModeContext = createContext({ mode: 'light', toggleColorMode: () => {} });

export const useColorMode = () => useContext(ColorModeContext);

const readStoredMode = () => {
  try {
    return localStorage.getItem(MODE_KEY) === 'true' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

export const AppThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(readStoredMode);

  const toggleColorMode = useCallback(() => {
    setMode((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(MODE_KEY, String(next === 'dark'));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  }, []);

  const theme = useMemo(() => createAppTheme(mode), [mode]);
  const value = useMemo(() => ({ mode, toggleColorMode }), [mode, toggleColorMode]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
